import { searchApi } from './search-client';
import { publicEndpoints } from '@/lib/endpoints/public';
import { pascalToCamelCaseObject } from '@/lib/utils';
import type {
  ContentSearchResponse,
  ContentSearchParams,
  ContentSuggestParams,
} from '@/lib/content/solr';

/**
 * Autocomplete suggestion DTO — mirrors the backend `AutocompleteSuggestionDto`.
 *
 * The autocomplete endpoint returns PascalCase JSON (Text, Type).
 * We transform via `pascalToCamelCaseObject` so the rest of the app uses camelCase.
 */
export interface AutocompleteSuggestion {
  text: string;
  type: string;
}

/**
 * Search CMS content (blog, FAQ, pages) via the Solr content search API.
 *
 * The backend returns PascalCase JSON (ASP.NET Core default), so we transform
 * via pascalToCamelCaseObject so the rest of the app can use camelCase.
 */
export const searchContent = async (
  params: ContentSearchParams,
): Promise<ContentSearchResponse> => {
  const endpoint = publicEndpoints.content.search({
    keywords: params.keywords,
    type: params.type,
    category: params.category,
    tags: params.tags,
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    orderBy: params.orderBy,
  });

  const response = await searchApi.get<ContentSearchResponse>(endpoint);
  return pascalToCamelCaseObject<ContentSearchResponse>(response.data);
};

/**
 * Get content title suggestions for autocomplete.
 *
 * Returns an array of suggestion strings (content titles matching the prefix).
 */
export const suggestContent = async (
  params: ContentSuggestParams,
): Promise<string[]> => {
  const endpoint = publicEndpoints.content.suggest(params.prefix, params.count);
  const response = await searchApi.get<string[]>(endpoint);
  return response.data;
};

/**
 * Get product autocomplete suggestions.
 *
 * Returns an array of AutocompleteSuggestion objects with text and type.
 * The backend response is PascalCase; we transform to camelCase.
 */
export const autocompleteProducts = async (
  query: string,
  count: number = 5,
): Promise<AutocompleteSuggestion[]> => {
  const endpoint = publicEndpoints.products.autocomplete(query, count);
  const response = await searchApi.get<AutocompleteSuggestion[]>(endpoint);
  return pascalToCamelCaseObject<AutocompleteSuggestion[]>(response.data);
};